/**
 * AddInvoiceRow.jsx - "Add an invoice", in the record editor's Invoicing
 * section (a project's): the invoice #, its amount and date. Add appends the
 * number to Invoice #'s, adds the amount to Invoiced, and moves Latest
 * Invoice Date forward - the Leftover follows. Nothing is saved until the
 * editor's Save; every field stays editable by hand (Sharif, 10/8/26).
 * Moved here from the retired Projects form (forms/ProjectForm).
 *
 * `f` / `setF` are the editor's form; `nextNumber()` offers the next invoice
 * number in the company's series. Module scope (a stable component type), so
 * its inputs keep focus while typing.
 */
import { useState } from 'react';
import { addInvoice } from '../../utils/projectMoney';
import { INPUT, LABEL } from '../forms/formStyles';
import { amount2 } from '../../utils/format';

export default function AddInvoiceRow({ f, setF, nextNumber, saveLabel = 'Save Changes' }) {
  const today = new Date().toLocaleDateString('en-CA'); // the local day, YYYY-MM-DD
  const [inv, setInv] = useState(() => ({ number: nextNumber() || '', amount: '', date: today }));
  const [msg, setMsg] = useState('');
  const add = () => {
    const out = addInvoice(f, inv);
    if (out.error) {
      setMsg(out.error);
      return;
    }
    setF((p) => ({ ...p, ...out }));
    setMsg(
      `Added${inv.number ? ` invoice ${inv.number}` : ''}${inv.amount ? ` for $${amount2(Number(inv.amount))}` : ''}. Click ${saveLabel} to save.`
    );
    const n = String(inv.number || '');
    // the next number in the same zero-padded series, ready for the next invoice
    const next = /^\d+$/.test(n) ? String(parseInt(n, 10) + 1).padStart(n.length, '0') : '';
    setInv({ number: next, amount: '', date: today });
  };
  const box = { ...INPUT, padding: '7px 10px' };
  return (
    <div style={{ marginTop: 10, padding: '12px 14px', background: '#F6F9F3', border: '1px solid #E0EAD8', borderRadius: 8 }}>
      <div style={{ fontSize: 10.5, fontWeight: 700, color: '#1B5E20', marginBottom: 8 }}>Add an invoice</div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label style={{ flex: '1 1 110px' }}>
          <span style={LABEL}>Invoice #</span>
          <input
            aria-label="New invoice number"
            style={box}
            value={inv.number}
            onChange={(e) => setInv((v) => ({ ...v, number: e.target.value }))}
            placeholder="e.g. 00617"
          />
        </label>
        <label style={{ flex: '1 1 110px' }}>
          <span style={LABEL}>Amount ($)</span>
          <input
            aria-label="New invoice amount"
            style={box}
            value={inv.amount}
            inputMode="decimal"
            onChange={(e) => setInv((v) => ({ ...v, amount: e.target.value.replace(/[^0-9.]/g, '') }))}
            placeholder="0.00"
          />
        </label>
        <label style={{ flex: '1 1 130px' }}>
          <span style={LABEL}>Date</span>
          <input
            aria-label="New invoice date"
            type="date"
            style={box}
            value={inv.date}
            onChange={(e) => setInv((v) => ({ ...v, date: e.target.value }))}
          />
        </label>
        <button
          type="button"
          onClick={add}
          style={{
            background: '#1B5E20',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            padding: '8px 16px',
            fontWeight: 700,
            fontSize: 11,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          Add
        </button>
      </div>
      {msg && (
        <div role="status" style={{ fontSize: 10.5, marginTop: 8, color: /must|Enter/.test(msg) ? '#B71C1C' : '#1B5E20' }}>
          {msg}
        </div>
      )}
    </div>
  );
}
